import React, { useContext, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Contexto de autenticação
import {Context}  from "../context/AuthContext"; // contexto global de autenticação
import { BoletimProvider } from "../context/BoletimContext";
import { isTokenExpired } from "../Utils/checkToken";
import { hasPermission } from "../Utils/permissions";
import api from "../Utils/api";

import Home from "../pages/Home/home";
import Header from "../pages/Boletim/Header/Header";
import Envolvidos from "../pages/Boletim/Envolvido/Envolvidos";
import ItensApreendidos from "../pages/Boletim/Material/ItensApreendidos";
import Efetivo from "../pages/Boletim/Efetivo/Efetivo";
import Historico from "../pages/Boletim/Historico/Historico";
import BoletimDetalhe from "../pages/Boletim/Detalhe/BoletimDetalhe";
import Page404 from "../components/Page404";

import RegisterUser from "../pages/Usuarios/RegisterUser";
import LoginUser from "../pages/Home/components/FormLogin";
import Perfil from "../pages/Usuarios/Perfil";
import EmailVerify from "../pages/Usuarios/EmailVerify";
import PasswordRecover from "../pages/Usuarios/PasswordRecover";

import Dashboard from "../pages/Dashboard/Dashboard";
import DashboardBoletimp from "../pages/Dashboard/Boletim/DashboardBoletim";
import DashboardUsuarios from "../pages/Admin/Usuarios/DashboardUsuarios";
import Naturezas from "../pages/Admin/Naturezas/Naturezas";
import Localidades from "../pages/Admin/Localidades/Localidades";
import OpcoesBoletim from "../pages/Admin/OpcoesBoletim/OpcoesBoletim";
import GerenciarBoletins from '../pages/Admin/Boletins/GerenciarBoletins';
import Estatistica from "../pages/Dashboard/Estatisticas/Estatistica";
import EstatisticasOperacionais from "../pages/Dashboard/Estatisticas/EstatisticasOperacionais";
import MapaOcorrencias from "../pages/Dashboard/Estatisticas/MapaOcorrencias";
import AnaliseAcidentes from "../pages/Dashboard/Estatisticas/AnaliseAcidentes";
import AnaliseArmas from '../pages/Dashboard/Estatisticas/AnaliseArmas';

import BoletimDetalheFromBD from "../pages/Boletim/BoletimDetalheFromBD";
import ListaBoletins from "../pages/Boletim/ListaBoletim";
import PublicValidationPage from "../pages/PublicValidationPage";
import AccessDenied from "../components/AccessDenied";
import BoletimSyncStatus from "../components/BoletimSyncStatus";
import BoletimEditGuard from '../components/BoletimEditGuard';
import BoletimById from '../components/BoletimById';
import PwaUpdateNotice from "../components/PwaUpdateNotice";

// -------------------------
// Rota privada genérica
// -------------------------
function PrivateRoute({ element, authenticated, authLoading, permission = null, anyPermissions = null }) {

  if (authLoading) return null;

  // VERIFICAÇÃO DO TOKEN
  if (isTokenExpired()) {
    localStorage.setItem("expired", "true"); // para mostrar mensagem no login
    localStorage.removeItem("x-access-token");
    localStorage.removeItem("x-user-id");
    localStorage.removeItem("x-user-mat-id");
    localStorage.removeItem("x-user-tipo");
    localStorage.removeItem("x-role-key");
    localStorage.removeItem("x-permissions");
    alert("Token expirado ou inválido. Faça login novamente!");
    return <Home />;
  }else{
     localStorage.setItem("expired", "false"); // reseta a flag de expirado
  }

  // VERIFICA LOGIN NORMAL
  if (!authenticated) return <Home />;

  const allowed = permission
    ? hasPermission(permission)
    : anyPermissions
      ? anyPermissions.some(hasPermission)
      : true;
  if (!allowed) return <AccessDenied />;

  return element;
}

// the main AppRoutes component
export default function AppRoutes() {
  const { authenticated, authLoading: contextAuthLoading, municipio, offlineMode } = useContext(Context);
  const [accessLoading, setAccessLoading] = useState(false);

  useEffect(() => {
    if (!authenticated) {
      localStorage.removeItem("x-permissions");
      localStorage.removeItem("x-role-key");
      setAccessLoading(false);
      return undefined;
    }

    let active = true;
    setAccessLoading(true);
    api.get("/auth/session")
      .then(({ data }) => {
        if (!active) return;
        localStorage.setItem("x-user-id", data.user.userId);
        localStorage.setItem("x-user-mat-id", data.user.userMatriculaId || "");
        localStorage.setItem("x-user-tipo", data.user.userTipo || "");
        localStorage.setItem("x-role-key", data.user.roleKey || "");
        localStorage.setItem("x-permissions", JSON.stringify(data.user.permissions || []));
      })
      .catch((error) => {
        if (active && !offlineMode && error.response) {
          localStorage.removeItem("x-permissions");
          localStorage.removeItem("x-role-key");
        }
      })
      .finally(() => {
        if (active) setAccessLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authenticated, offlineMode]);

  const authLoading = contextAuthLoading || accessLoading;

  return (
    <BrowserRouter>
      <BoletimProvider>
        <BoletimSyncStatus />
        <PwaUpdateNotice />
        <Routes>
          {/* Home */}
          <Route path="/" element={<Home />} />
          <Route path="/validar-boletim/:token" element={<PublicValidationPage />} />

          {/* Rotas de Boletim */}
          <Route path="/boletim">
            <Route
              path="header"
              element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.create", "boletins.edit.own", "boletins.edit.all"]} element={<BoletimEditGuard><Header municipio={municipio} /></BoletimEditGuard>} />}
    
            />
            <Route
              path="envolvido"
              element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.create", "boletins.edit.own", "boletins.edit.all"]} element={<BoletimEditGuard><Envolvidos /></BoletimEditGuard>} />}
            />
            <Route
              path="material"
              element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.create", "boletins.edit.own", "boletins.edit.all"]} element={<BoletimEditGuard><ItensApreendidos /></BoletimEditGuard>} />}
            />
            <Route
              path="efetivo"
              element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.create", "boletins.edit.own", "boletins.edit.all"]} element={<BoletimEditGuard><Efetivo /></BoletimEditGuard>} />}
            />
            <Route
              path="historico"
              element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.create", "boletins.edit.own", "boletins.edit.all"]} element={<BoletimEditGuard><Historico /></BoletimEditGuard>} />}
            />
            <Route
              path="VerBoletim"
              element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.create", "boletins.edit.own", "boletins.edit.all"]} element={<BoletimEditGuard><BoletimDetalhe /></BoletimEditGuard>} />}
            />
          </Route>

          {/* Área administrativa */}
          <Route path="/boletins/:id" element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.view.own", "boletins.view.all"]} element={<BoletimById />} />} />
          <Route path="/administrador">
            <Route path="opcoes-boletim" element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["naturezas.manage", "localidades.manage"]} element={<OpcoesBoletim />} />} />
            <Route path="boletins" element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="boletins.admin.view" element={<GerenciarBoletins />} />} />
            <Route path="boletins/duplicados" element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="boletins.admin.view" element={<GerenciarBoletins />} />} />
            <Route path="boletins/excluidos" element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="boletins.admin.view" element={<GerenciarBoletins />} />} />
            <Route
              path="usuarios"
              element={
                <PrivateRoute
                  authenticated={authenticated}
                  authLoading={authLoading}
                  permission="users.view"
                  element={<DashboardUsuarios />}
                />
              }
            />
            <Route
              path="naturezas"
              element={
                <PrivateRoute
                  authenticated={authenticated}
                  authLoading={authLoading}
                  permission="naturezas.manage"
                  element={<Naturezas />}
                />
              }
            />
            <Route
              path="localidades"
              element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="localidades.manage" element={<Localidades />} />}
            />
          </Route>

          {/* Usuários */}
          <Route path="/registro" element={<RegisterUser />} />
          <Route path="/login" element={<LoginUser />} />
          <Route path="/emailverify" element={<EmailVerify />} />
          <Route path="/passwordrecover/:id" element={<PasswordRecover />} />
          <Route
            path="/perfil"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="dashboard.view" element={<Perfil />} />}
          />

          {/* Dashboards */}
          <Route
            path="/dashboard"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="dashboard.view" element={<Dashboard />} />}
          />
          <Route
            path="/dashboard/boletim"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.view.own", "boletins.view.all"]} element={<DashboardBoletimp />} />}
          />
          <Route 
            path="/dashboard/boletim/list"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} element={<ListaBoletins />} permission="boletins.view.all" />}
          />

          <Route
            path="/dashboard/estatisticas"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="statistics.view" element={<Estatistica />} />}
          />
          <Route
            path="/dashboard/estatisticas/operacionais"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="statistics.view" element={<EstatisticasOperacionais />} />}
          />
          <Route path="/dashboard/estatisticas/acidentes" element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="statistics.view" element={<AnaliseAcidentes />} />} />
          <Route path="/dashboard/estatisticas/armas" element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="statistics.view" element={<AnaliseArmas />} />} />
          <Route
            path="/dashboard/estatisticas/geral"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="statistics.view" element={<Navigate to="/dashboard/estatisticas" replace />} />}
          />
          <Route
            path="/dashboard/estatisticas/mapa"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="map.view" element={<MapaOcorrencias />} />}
          />
          <Route
            path="/dashboard/violencia-domestica"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} permission="statistics.view" element={<Navigate to="/dashboard/estatisticas" replace />} />}
          />

          {/* Outros */}
          <Route
            path="/BoFromBD"
            element={<PrivateRoute authenticated={authenticated} authLoading={authLoading} anyPermissions={["boletins.view.own", "boletins.view.all"]} element={<BoletimDetalheFromBD />} />}
          />

          {/* 404 */}
          <Route path="*" element={<Page404 />} />
        </Routes>
      </BoletimProvider>
    </BrowserRouter>
  );
}
